import { call, put, select, takeEvery } from 'typed-redux-saga';

import { iDBProjectService } from '../../resources/iDBProject';
import { IAction } from '../../types/redux';
import { beginSession, endActiveSession, peekActiveSession } from '../../utils/SyncGroup/SyncGroup';
import * as layoutGate from '../../utils/editorLayout/editorLayout.gate';
import { sanitizeLayout } from '../../utils/editorLayout/editorLayout.sanitize';
import { loadLayout } from '../../utils/editorLayout/editorLayout.storage';
import { resetContentWindow } from '../contentWindows';
import { restoreLayout } from '../layout';
import { countForProjectNotes } from '../stats';
import { resetSyncState, syncReconcile } from '../sync/sync.actions';
import { TAppState } from '../types';

import { setSelectedSecondaryNode } from './editor.actions';
import {
  APPLY_EVENTS,
  IEventsApplyAction,
  INNER_APPLY_EVENTS,
  INNER_APPLY_REMOTE,
  IProjectSetAction,
  PROJECT_CLEAN,
  PROJECT_SET,
} from './editor.types';

export function* setProjectEffect(action: IAction<IProjectSetAction>) {
  console.log('setProjectEffect', action);

  if (!action.payload?.project || !action.payload?.project.id) {
    return;
  }

  // Синхронно с PROJECT_SET открываем новую сессию (закрывает предыдущую до любого
  // async). Слайс сбрасываем на новый токен (несёт generation), затем — reconcile.
  const session = beginSession(action.payload.project.id);

  yield* put(resetSyncState({ session: session.token }));
  yield* put(syncReconcile({ reason: 'project-set' }));

  const { project } = action.payload;
  const noteContentList = Object.entries(project.notes).map(([id, note]) => ({
    id,
    content: note.content,
  }));

  yield* put(
    countForProjectNotes({
      list: noteContentList,
    }),
  );

  // Восстановление layout'а не зависит от сети: читаем локально и диспатчим сразу.
  // markLayoutRestored обязан идти НЕПОСРЕДСТВЕННО перед put — до этого момента
  // подписка не имеет права писать, потому что в сторе ещё окна прошлого проекта.
  const layout = sanitizeLayout(loadLayout(project.id), project);

  layoutGate.markLayoutRestored(project.id);

  yield* put(restoreLayout({ layout }));
}

export function* applyEventsEffect(action: IAction<IEventsApplyAction>) {
  console.log('applyEventsEffect', action);
  try {
    if (!action.payload) {
      return;
    }

    const { project } = yield* select((state: TAppState) => state.editor);

    if (!project) {
      return;
    }

    const session = peekActiveSession();
    // Правка адресована уже не активному проекту → консистентно НЕ применяем ни к
    // снапшоту, ни к очереди (data-guard, §2.3). Полная дропа до INNER-put.
    if (!session || session.token.projectId !== project.id) {
      console.warn('applyEventsEffect: no current session for project — event dropped');
      return;
    }

    yield* put({ ...action, type: INNER_APPLY_EVENTS });

    // Дожидаемся гидратации, чтобы прочитать снапшот и получить train.
    yield* call(() => session.ready);

    // Заряжаем в train ИМЕННО этой сессии (проекта, к которому относится правка),
    // даже если мы уже переключились на другой проект. quiesce-charge durable-кладёт
    // событие в очередь этого проекта, и оно синхронизируется при следующем открытии —
    // без тихой потери (C4). НЕ гейтим на isCurrent(): правка принадлежит своему
    // проекту независимо от того, активен ли он ещё.
    // Живой флаг isSyncEnabled может опустить disable в течение сессии (destroySyncWorker).
    const snapshot = session.syncStateSnapshot();
    const state =
      snapshot.kind === 'enabled' || snapshot.kind === 'disabled' ? snapshot.state : null;
    if (!state?.isSyncEnabled) {
      return;
    }

    session.train?.charge(action.payload);
  } catch (error) {
    console.log('Error during applyEventsEffect', error);
  }
}

function* innerApplyEventsEffect(action: IAction<IEventsApplyAction>) {
  console.log('innerApplyEventsEffect', action);
  try {
    if (!action.payload) {
      return;
    }

    const { project } = yield* select((state: TAppState) => state.editor);

    if (!project) {
      return;
    }
    yield* call(iDBProjectService.saveOne, project);
  } catch (error) {
    console.log('Error during innerApplyEventsEffect', error);
  }
}

// Персист проекта после батч-применения remote-событий (delta). Раньше проект
// сохранялся по одному разу на КАЖДОЕ per-event INNER_APPLY_EVENTS; теперь — один
// раз после батча (реже, но эквивалентно по итоговому состоянию).
function* innerApplyRemoteEffect() {
  try {
    const { project } = yield* select((state: TAppState) => state.editor);
    if (!project) {
      return;
    }
    yield* call(iDBProjectService.saveOne, project);
  } catch (error) {
    console.log('Error during innerApplyRemoteEffect', error);
  }
}

export function* cleanProjectEffect() {
  yield* call(endActiveSession);

  // Гейт закрываем первым: с этого момента подписка не должна писать ничего.
  layoutGate.resetLayoutRestoreGate();

  yield* put(setSelectedSecondaryNode({ secondarySelected: null }));
  // Без этого окна предыдущего проекта оставались висеть при выходе в список.
  yield* put(resetContentWindow());
}

export function* editorSagaWatch() {
  yield* takeEvery(PROJECT_SET, setProjectEffect);
  yield* takeEvery(PROJECT_CLEAN, cleanProjectEffect);
  yield* takeEvery(APPLY_EVENTS, applyEventsEffect);
  yield* takeEvery(INNER_APPLY_EVENTS, innerApplyEventsEffect);
  yield* takeEvery(INNER_APPLY_REMOTE, innerApplyRemoteEffect);
}
